import type { ApiWatchlist, WatchlistFlagColor, WatchlistImportResponse } from '../../types/api';
import { apiUrl } from '../../config/runtime';
import { apiFetch } from './client';

export function fetchWatchlists(): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>('/api/watchlists', { cache: 'no-store' });
}

export function createWatchlist(name: string): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>('/api/watchlists', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function renameWatchlist(id: string, name: string): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/${encodeURIComponent(id)}` as `/api/${string}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

export function setWatchlistPinned(id: string, pinned: boolean): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/${encodeURIComponent(id)}/pin` as `/api/${string}`, {
    method: 'PUT',
    body: JSON.stringify({ pinned }),
  });
}

export function deleteWatchlist(id: string): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/${encodeURIComponent(id)}` as `/api/${string}`, {
    method: 'DELETE',
  });
}

export function addWatchlistSymbol(watchlistId: string, symbol: string): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/${encodeURIComponent(watchlistId)}/symbols/${encodeURIComponent(symbol)}` as `/api/${string}`, {
    method: 'POST',
  });
}

export function importWatchlistFile(file: File): Promise<WatchlistImportResponse> {
  const formData = new FormData();
  formData.set('file', file);
  return apiFetch<WatchlistImportResponse>('/api/watchlists/import', {
    method: 'POST',
    body: formData,
  });
}

export function addWatchlistSection(watchlistId: string, name: string): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/${encodeURIComponent(watchlistId)}/sections` as `/api/${string}`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function renameWatchlistItem(itemId: string, name: string): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/items/${encodeURIComponent(itemId)}` as `/api/${string}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

export function setWatchlistItemFlag(itemId: string, flagColor: WatchlistFlagColor | null): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/items/${encodeURIComponent(itemId)}/flag` as `/api/${string}`, {
    method: 'PUT',
    body: JSON.stringify({ flag_color: flagColor }),
  });
}

export function deleteWatchlistItem(itemId: string): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/items/${encodeURIComponent(itemId)}` as `/api/${string}`, {
    method: 'DELETE',
  });
}

export function updateWatchlistOrder(watchlistId: string, itemIds: string[]): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>(`/api/watchlists/${encodeURIComponent(watchlistId)}/order` as `/api/${string}`, {
    method: 'PUT',
    body: JSON.stringify({ item_ids: itemIds }),
  });
}

export function updateWatchlistsOrder(watchlistIds: string[]): Promise<ApiWatchlist[]> {
  return apiFetch<ApiWatchlist[]>('/api/watchlists/order', {
    method: 'PUT',
    body: JSON.stringify({ watchlist_ids: watchlistIds }),
  });
}

export async function importLocalWatchlist(watchlist: ApiWatchlist): Promise<{ watchlists: ApiWatchlist[]; createdWatchlistId: string }> {
  let rows = await createWatchlist(watchlist.name || 'Watchlist');
  const created = rows.find((candidate) => candidate.name === (watchlist.name || 'Watchlist')) ?? rows[rows.length - 1];
  if (!created) throw new Error('Watchlist import failed');

  const importedItemIds: string[] = [];
  let current = created;

  for (const item of watchlist.items) {
    const previousItemIds = new Set(current.items.map((candidate) => candidate.id));
    if (item.item_type === 'section') {
      rows = await addWatchlistSection(created.id, item.name || 'Section');
    } else if (item.symbol) {
      rows = await addWatchlistSymbol(created.id, item.symbol);
    } else {
      continue;
    }

    current = rows.find((candidate) => candidate.id === created.id) ?? current;
    const createdItem = current.items.find((candidate) => !previousItemIds.has(candidate.id));
    if (!createdItem) continue;
    importedItemIds.push(createdItem.id);

    if (item.item_type === 'symbol' && item.flag_color) {
      rows = await setWatchlistItemFlag(createdItem.id, item.flag_color);
      current = rows.find((candidate) => candidate.id === created.id) ?? current;
    }
  }

  if (importedItemIds.length > 0) {
    rows = await updateWatchlistOrder(created.id, importedItemIds);
  }

  return { watchlists: rows, createdWatchlistId: created.id };
}

export function createWatchlistEventSource(): EventSource {
  return new EventSource(apiUrl('/api/watchlists/events'), { withCredentials: true });
}
